import React, { Component } from "react";
import { toast } from "react-toastify";
import {
  getCrossSellRules,
  toggleCrossSellRule,
  deleteCrossSellRule,
  getBranchList,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Btn, ChipTabs, Table, Chip, Toggle, IconBtn, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import { canWrite, isBranchScopedAdmin, getUserBranchName } from "../../services/PermissionService";
import { CONTEXT_LABEL, KIND_LABEL, scheduleSummary } from "./crossSellMeta";

/**
 * Cross-sell rules — the offers an admin pins to moments in the guest app.
 *
 * A rule only ever overrides the built-in suggestions (paired dishes, airport
 * rides, breakfast...) for its own offer kind, and the highest priority wins
 * when two rules compete, so the list is sorted the way the server ranks them.
 *
 * Branch scope is the server's: a branch admin gets only their branch's rules,
 * a global admin the switcher's branch (plus hotel-wide rules) or all.
 */

const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

/** Live / Paused / Scheduled / Ended — "active" alone hides a rule outside its dates. */
const ruleState = (rule, now = new Date()) => {
  if (!rule.active) return { label: "Paused", tone: "neutral" };
  if (rule.endsAt && new Date(rule.endsAt) < now) return { label: "Ended", tone: "bad" };
  if (rule.startsAt && new Date(rule.startsAt) > now) return { label: "Scheduled", tone: "warn" };
  return { label: "Live", tone: "good" };
};

class CrossSellRules extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      rules: [],
      branches: [],
      tab: "all",
      togglingId: null,
      deleting: null,
      deleteBusy: false,
    };
  }

  componentDidMount() {
    this.loadRules();
    if (!isBranchScopedAdmin()) {
      getBranchList().then((res) => {
        if (res && res.success && Array.isArray(res.data)) this.setState({ branches: res.data });
      });
    }
  }

  loadRules = () => {
    getCrossSellRules().then((res) => {
      if (res && res.status === 200) {
        this.setState({ isLoaded: true, rules: res.data || [] });
      } else {
        this.setState({ isLoaded: true });
        toast.error((res && res.message) || "Could not load the cross-sell rules");
      }
    });
  };

  handleToggle = (rule) => {
    this.setState({ togglingId: rule._id });
    toggleCrossSellRule(rule._id, !rule.active).then((res) => {
      this.setState({ togglingId: null });
      if (res && res.status === 200) {
        toast.success(res.message);
        this.setState((prev) => ({
          rules: prev.rules.map((r) => (r._id === rule._id ? { ...r, active: res.data.active } : r)),
        }));
      } else {
        toast.error((res && res.message) || "Could not update the rule");
      }
    });
  };

  confirmDelete = () => {
    const rule = this.state.deleting;
    this.setState({ deleteBusy: true });
    deleteCrossSellRule(rule._id).then((res) => {
      this.setState({ deleteBusy: false });
      if (res && res.status === 200) {
        toast.success("Rule deleted");
        this.setState((prev) => ({ deleting: null, rules: prev.rules.filter((r) => r._id !== rule._id) }));
      } else {
        toast.error((res && res.message) || "Could not delete the rule");
      }
    });
  };

  branchText(rule) {
    const ids = rule.branchIds || [];
    if (isBranchScopedAdmin()) return getUserBranchName() || "Your branch";
    if (!ids.length) return "All branches";
    const names = ids.map((id) => {
      const b = this.state.branches.find((x) => String(x._id) === String(id));
      return b ? b.name || b.code : null;
    });
    return names.every(Boolean) && names.length <= 2 ? names.join(", ") : `${ids.length} branches`;
  }

  columns(write) {
    return [
      {
        key: "name",
        label: "Rule",
        render: (r) => (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>{r.name}</div>
            {r.offer && r.offer.title && (
              <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>“{r.offer.title}”</div>
            )}
          </div>
        ),
      },
      {
        key: "offer",
        label: "Offers",
        render: (r) => {
          const kind = KIND_LABEL[r.offer && r.offer.kind];
          const n = (r.offer && r.offer.refIds && r.offer.refIds.length) || 0;
          const discount = r.offer && r.offer.discountType
            ? r.offer.discountType === "percentage" ? `${r.offer.discountValue}% off` : `$${r.offer.discountValue} off`
            : r.offer && r.offer.couponId ? "With coupon" : null;
          return (
            <div>
              <Chip tone="neutral">
                {kind && <span className="ms" style={{ fontSize: 15 }}>{kind.icon}</span>}
                {n} {kind ? (n === 1 ? kind.one : kind.label.toLowerCase()) : "items"}
              </Chip>
              {discount && <div style={{ marginTop: 5, fontSize: 12, color: C.taupe }}>{discount}</div>}
            </div>
          );
        },
      },
      {
        key: "contexts",
        label: "Shown at",
        render: (r) => {
          const labels = (r.contexts || []).map((c) => CONTEXT_LABEL[c] || c);
          return (
            <span style={{ fontSize: 13, color: C.slate }}>
              {labels.slice(0, 2).join(", ")}
              {labels.length > 2 && ` +${labels.length - 2}`}
            </span>
          );
        },
      },
      {
        key: "when",
        label: "When",
        render: (r) => (
          <div style={{ fontSize: 13, color: C.slate }}>
            {scheduleSummary(r.schedule)}
            {(r.startsAt || r.endsAt) && (
              <div style={{ marginTop: 3, fontSize: 12, color: C.taupeLight }}>
                {r.startsAt ? fmtDay(r.startsAt) : "Now"} → {r.endsAt ? fmtDay(r.endsAt) : "no end"}
              </div>
            )}
          </div>
        ),
      },
      { key: "branches", label: "Branches", render: (r) => <span style={{ fontSize: 13, color: C.slate }}>{this.branchText(r)}</span> },
      { key: "priority", label: "Priority", width: 80, render: (r) => <span style={{ color: C.ink }}>{r.priority || 0}</span> },
      {
        key: "state",
        label: "Status",
        width: 150,
        render: (r) => {
          const st = ruleState(r);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
              <Toggle
                on={r.active}
                disabled={!write || this.state.togglingId === r._id}
                title={r.active ? "Pause this rule" : "Switch this rule on"}
                onClick={() => write && this.handleToggle(r)}
              />
              <Chip tone={st.tone}>{st.label}</Chip>
            </div>
          );
        },
      },
      {
        key: "actions",
        label: "",
        width: 90,
        render: (r) => (
          <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <IconBtn icon={write ? "edit" : "visibility"} title={write ? "Edit" : "Open"} onClick={() => this.openRule(r)} />
            {write && <IconBtn icon="delete" tone="bad" title="Delete" onClick={() => this.setState({ deleting: r })} />}
          </div>
        ),
      },
    ];
  }

  openRule = (rule) => this.props.history.push(`/crosssell/edit/${rule._id}`);

  render() {
    const { isLoaded, rules, tab, deleting, deleteBusy } = this.state;
    const write = canWrite();
    const now = new Date();
    const live = rules.filter((r) => ruleState(r, now).label === "Live").length;
    const paused = rules.filter((r) => !r.active).length;
    const visible = rules.filter((r) =>
      tab === "all" ? true : tab === "live" ? ruleState(r, now).label === "Live" : !r.active
    );

    return (
      <ConsolePage
        title="Cross-sell offers"
        subtitle={isLoaded ? `${rules.length} rule${rules.length === 1 ? "" : "s"} · ${live} live` : "Loading rules…"}
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="monitoring" onClick={() => this.props.history.push("/crosssell/report")}>
              Report
            </Btn>
            {write && (
              <Btn icon="add" onClick={() => this.props.history.push("/crosssell/new")}>
                New rule
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Panel
            title="Rules"
            subtitle="With no rule for a moment, the app falls back to built-in suggestions — dishes that go well together, airport rides, breakfast and tables."
          >
            <div style={{ marginTop: 16 }}>
              <ChipTabs
                value={tab}
                onChange={(v) => this.setState({ tab: v })}
                options={[
                  { value: "all", label: "All", count: rules.length },
                  { value: "live", label: "Live now", count: live },
                  { value: "paused", label: "Paused", count: paused },
                ]}
              />
            </div>
            <div style={{ marginTop: 14 }}>
              {isLoaded ? (
                <Table
                  columns={this.columns(write)}
                  rows={visible}
                  onRowClick={this.openRule}
                  empty={rules.length ? "No rules in this view" : "No rules yet — the app is showing built-in suggestions only"}
                />
              ) : (
                <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>Loading rules…</div>
              )}
            </div>
          </Panel>
        </div>

        <ConfirmDialog
          isOpen={!!deleting}
          toggle={() => !deleteBusy && this.setState({ deleting: null })}
          onConfirm={this.confirmDelete}
          loading={deleteBusy}
          title={`Delete “${deleting ? deleting.name : ""}”?`}
          body="Guests stop seeing this offer straight away. Its numbers stay in the cross-sell report."
          confirmText="Delete rule"
        />
      </ConsolePage>
    );
  }
}

export default CrossSellRules;
